import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgbCalendar, NgbDate, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';

/**
 * Simple component for displaying customizable confirm modal. Based on ngx-bootstrap and PrimeNG
 */
@Component({
  selector: 'qm-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss']
})
export class DateRangePickerComponent implements OnInit {
  @Input()
  public range: [NgbDate, NgbDate] = [
    this.calendar.getPrev(this.calendar.getToday(), 'm', 12),
    this.calendar.getToday(),
  ];
  @Output()
  public rangeChange = new EventEmitter<this['range']>();

  private hoveredDate: NgbDate | null = null;

  // these are used during the selection process,
  // `range` always contains a valid range
  public fromDate: NgbDate | null = this.range[0];
  public toDate: NgbDate | null = this.range[1];

  constructor(
    private calendar: NgbCalendar,
    public formatter: NgbDateParserFormatter,
  ) { }

  ngOnInit(): void {
    // emit the initial value to keep things consistent
    this.rangeChange.emit(this.range);
  }

  private updateRange(): void {
    // skip if there is no change
    if (this.fromDate.equals(this.range[0]) && this.toDate.equals(this.range[1])) {
      return;
    }
    this.range = [this.fromDate, this.toDate];
    this.rangeChange.emit(this.range);
  }

  public onDateSelection(date: NgbDate): void {
    if (!date) {
      return;
    }

    if (this.fromDate && !this.toDate) {
      this.toDate = date;

      if (this.fromDate.after(this.toDate)) {
        [this.fromDate, this.toDate] = [this.toDate, this.fromDate];
      }

      this.updateRange();
    } else {
      this.fromDate = date;
      this.toDate = null;
    }
  }

  public isHovered(date: NgbDate): boolean {
    return this.fromDate && !this.toDate && this.hoveredDate && (
      date.after(this.fromDate) && date.before(this.hoveredDate)
      || date.before(this.fromDate) && date.after(this.hoveredDate)
    );
  }

  public isInside(date: NgbDate): boolean {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  public isRange(date: NgbDate): boolean {
    return date.equals(this.fromDate) || (this.toDate && date.equals(this.toDate)) || this.isInside(date) || this.isHovered(date);
  }

  public manualInput(field: 'fromDate' | 'toDate', input: string): void {
    const parsed = this.formatter.parse(input);
    if (parsed && this.calendar.isValid(NgbDate.from(parsed))) {
      this[field] = NgbDate.from(parsed);
      this.updateRange();
    }
  }
}
